import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { parseTenantActivationFilters, useTenantActivations } from "@visioni/a3s-monitoring/app";
import { patriotToolsHref } from "../routes";
import { useActivationScopeOptions } from "../hooks/useActivationScopeOptions";
import { ActivationFilterBar, type ActivationFilterValues } from "../components/ActivationFilterBar";
import { ActivationsTable } from "../components/ActivationsTable";
import { QueryNotice } from "../components/QueryNotice";

/**
 * Filters live in the URL (`?site=&camera=&from=&to=`) so a filtered list can be
 * shared. The page does not: a cursor is an opaque replica key, and a shared
 * link should open on the newest activations.
 */
export function TenantActivationsScreen() {
  const router = useRouter();
  const { tenantId, site, camera, from, to } = useLocalSearchParams<{
    tenantId: string;
    site?: string;
    camera?: string;
    from?: string;
    to?: string;
  }>();
  const parsed = parseTenantActivationFilters({ site, camera, from, to });
  const filters = parsed.ok ? parsed.filters : {};

  const scope = useActivationScopeOptions();
  const list = useTenantActivations({ filters, enabled: parsed.ok });

  const onFiltersChange = (next: ActivationFilterValues) => {
    // Every key is always passed, so a cleared filter is removed from the URL
    // rather than left behind from the previous selection.
    router.setParams({ site: next.site, camera: next.camera, from: next.from, to: next.to });
  };

  return (
    <View style={styles.page}>
      <View style={styles.filterRow}>
        <ActivationFilterBar
          values={{
            ...(site && { site }),
            ...(camera && { camera }),
            ...(from && { from }),
            ...(to && { to }),
          }}
          onChange={onFiltersChange}
          sites={scope.sites}
          cameras={scope.cameras}
          isLoading={scope.isLoading}
        />
      </View>
      {!parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No activations found
          </Text>
        </View>
      : <ActivationsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(activation) =>
            patriotToolsHref(tenantId, `tenant-activations/${encodeURIComponent(activation.ticketNo)}`)
          }
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filterRow: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the filter dropdowns open over the rows.
    zIndex: 1,
  },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
